import { useContext, useMemo } from 'react';
import _ from 'underscore';

import { ServerContext } from '@bingads-webui-campaign-react/mca-server-context';

import { isSiEnvironment } from './get-environment';

export const getPilotValue = (serverContext, key) => {
  const cmuiPilotKey = key && key.replace(/^is/, 'Is');
  const ccuiPilotKey = key && key.replace(/^Is/, 'is');
  const uflPilotKey = key && key.replace(/^(is|Is)/, '').replace(/Enabled$/, ''); // ufl stands for Unified Feature Library

  const cmuiPilotValue = _.result(serverContext.Permissions, cmuiPilotKey, false);
  const ccuiPilotValue = _.result(serverContext.CCUIPilots, ccuiPilotKey, false);
  const uflPilotValue = _.result(serverContext.DynamicPermissions, uflPilotKey, false);

  return cmuiPilotValue || ccuiPilotValue || uflPilotValue;
};

export function usePilotsValue(key) {
  const [serverContext] = useContext(ServerContext);
  const isPilotEnabled = useMemo(
    () => (key ? getPilotValue(serverContext, key) : false),
    [key, serverContext]
  );

  return isPilotEnabled;
}

export function useAdEditorialPilotValue() {
  return usePilotsValue('IsDMCAdEditorialEnabled');
}

export const useDefaultSignupSearchAdsOnlyPilotValue = () => usePilotsValue('IsDMCDefaultSignupSearchAdsOnlyEnabled');
export const useManagedOnboardingPilotValue = () => usePilotsValue('IsDMCManagedOnboardingEnabled');
export const useDMCSmartPagePilotValue = () => usePilotsValue('IsDMCSmartPageEnabled');
export const useDMCLinkedinConnectPilotValue = () => usePilotsValue('IsDMCLinkedinConnectEnabled');
export const useIsReadOnlyUser = () => usePilotsValue('IsReadOnlyUser');

export const useIsInternalUser = () => {
  const [serverContext] = useContext(ServerContext);
  if (serverContext.CurrentUser && !_.isUndefined(serverContext.CurrentUser.IsInternal)) {
    return serverContext.CurrentUser.IsInternal;
  }
  return serverContext.IsInternal || false;
};

export function useIsSubscriptionAdsEnabled() {
  const signup = usePilotsValue('IsSubscriptionSignupEnabled');
  const postSignup = usePilotsValue('IsUnifiedProductSubscriptionEnabled');

  return signup || postSignup;
}

export const useIsSubscriptionAdsPhase2Enabled = () => {
  // TODO: add SubscriptionAdsPhase2 pilot value here
  // Warning: this is just in case, or for quick comparison, not for general use
  const urlPilot = window.location.search.match(/(forcesubads2=.*true.*|forcesubads2=1)/);

  return !!urlPilot;
};

export const useIsSubscriptionAdsProduct = () => {
  const subAdsPhase1PilotEnabled = useIsSubscriptionAdsEnabled();
  const subAdsPhase2PilotEnabled = useIsSubscriptionAdsPhase2Enabled();
  const isSubscriptionAds = subAdsPhase1PilotEnabled || subAdsPhase2PilotEnabled;
  return isSubscriptionAds;
};

export const useDMCBWTIntegrationPilotValue = () => {
  // The DMCBWTIntegration pilot and ManagedOnboarding pilot need to be turned on at the same time
  const DMCBWTPilotValue = usePilotsValue('IsDMCBWTIntegrationEnabled');
  const managedOnboardingPilotValue = useManagedOnboardingPilotValue();
  return DMCBWTPilotValue && managedOnboardingPilotValue;
};
export const useIsShowCouponDetailsEnabled = () => usePilotsValue('IsShowCouponDetailsEnabled');
export const useUnifiedProductPhase2Value = () => usePilotsValue('isUnifiedProductPhase2Enabled');

export const useIsUPStepWizardEnabled = () => {
  const isSubscriptionAdsProduct = useIsSubscriptionAdsProduct();
  const value = usePilotsValue('isUnifiedProductStepWizardEnabled');
  const dbPilot = usePilotsValue('UPStepWizardDB');

  if (isSubscriptionAdsProduct) {
    return false;
  }

  // Warning: this is just in case, or for quick comparison, not for general use
  const forcePilot = window.location.search.match(/(forceupsw=.*true.*|forceupsw=1)/);

  // noupsw is a query that suppresses the pilot
  const noupswPilot = window.location.search.match(/(noupsw)/);

  return !noupswPilot && (value || dbPilot || !!forcePilot);
};

export const useIsBingAdsOptional = () => usePilotsValue('isBingAdsOptionalEnabled');
export const useSmartUIPilotValue = () => usePilotsValue('isSmartUIEnabled');
export const useIsInstagramPublishEnabled = () => usePilotsValue('IsDMCInstagramPublishEnabled');
export const useSmartPageUPIntegrationValue = () => usePilotsValue('isSmartPageUPIntegrationEnabled');
export const useSmartPageCoverImageCropperValue = () => usePilotsValue('isSmartPageCoverImageCropperEnabled');
export const useSmartPageRichTextEditorValue = () => usePilotsValue('isSmartPageRichTextEditorEnabled');
export const useUPNewExpertPilotValue = () => usePilotsValue('isUPNewExpertEnabled');
export const useNewExpertSignUpPilotValue = () => usePilotsValue('isNewExpertSignUpEnabled');
export const useSignupWithSmartGoogleImportPilotValue = () => usePilotsValue('isSignupWithSmartGoogleImportEnabled');
export const useUPHideDraftValue = () => usePilotsValue('isUPHideDraftEnabled');
export const useCustomEventConversionTrackingValue = () => usePilotsValue('IsDMCCustomEventConversionTrackingEnabled');
export const useUnifiedProductAgeAndGenderValue = () => usePilotsValue('isUnifiedProductAgeAndGenderEnabled');
export const useIsSmartPageCustomEventTrackingEnabled = () => usePilotsValue('isSmartPageCustomEventTrackingEnabled');
export const useIsSmartPageUPConversionTrackingEnabled = () => usePilotsValue('isSmartPageUPConversionTrackingEnabled');
export const useIsOmnichannelChatEnabled = () => usePilotsValue('IsOmnichannelChatEnabled');
export const useM365Enabled = () => usePilotsValue('IsUnifiedProductM365IntegrationEnabled');
export const useIsNewImageCreatorEnabled = () => usePilotsValue('IsUnifiedProductNewImageCreatorEnabled');

export function useDmcCouponPilotValue() {
  return usePilotsValue('isDMCCouponEnabled');
}

export function useAutoAdsSuggestionPilotValue() {
  return usePilotsValue('IsDMCAutoAdsSuggestionEnabled');
}

export function useHomePageV2PilotValue() {
  return usePilotsValue('IsDMCHomePageV2Enabled');
}

export function useHomeTipsTileEnabledValue() {
  return usePilotsValue('IsDMCHomeTipsTileEnabled');
}

export function useSupportChatAndCallPilotValue() {
  return usePilotsValue('isDMCSupportChatAndCallEnabled');
}

export function isRequestCallEnabled(serverContext, isUnifiedProduct) {
  return serverContext.SupportOptions.IfShowRequestCall &&
    (isUnifiedProduct || getPilotValue(serverContext, 'isDMCSupportChatAndCallEnabled'));
}

export function isRequestChatEnabled(serverContext, isUnifiedProduct) {
  return serverContext.SupportOptions.IfShowLiveChat &&
    (isUnifiedProduct || getPilotValue(serverContext, 'isDMCSupportChatAndCallEnabled'));
}

export function useDMCCouponClassAndPBCPilotValue() {
  return usePilotsValue('isDMCCouponClassAndPBCEnabled');
}

export function useDMCFloatingHelpPilotValue() {
  return usePilotsValue('isDMCFloatingHelpEnabled');
}

export function useFullyManagedFacebookAdAccountPilotValue() {
  // NOTE: We check for 2 pilots because there's special sign-up logic in CCUI to handle adding
  // newly created customers to the DB pilot
  const signUpBundlePilot = usePilotsValue('isManagedFacebookAdAccountEnabled');
  const mainBundlePilot = usePilotsValue('isUnifiedProductFullyManagedFacebookAdAccountEnabled');
  const isSubscriptionAdsProduct = useIsSubscriptionAdsProduct();
  const isStepWizardEnabled = useIsUPStepWizardEnabled();

  return signUpBundlePilot || mainBundlePilot || isSubscriptionAdsProduct || isStepWizardEnabled;
}

export function useSmartPageStandaloneRedirectionPilotValue() {
  return usePilotsValue('isSmartPageStandaloneRedirectionEnabled');
}

export function useShowUpBudgetBar() {
  const isUpBudgetBarEnabled = usePilotsValue('isUnifiedProductBudgetBarEnabled');
  const isSubscriptionAdsEnabled = useIsSubscriptionAdsEnabled();

  // hide UP budget bar when isSubscriptionAdsEnabled is true
  return isUpBudgetBarEnabled && !isSubscriptionAdsEnabled;
}

export function useShowUpCouponPrompt() {
  const isUpCouponPromptEnabled = usePilotsValue('isUnifiedProductCouponPromptEnabled');
  const isSubscriptionAdsEnabled = useIsSubscriptionAdsEnabled();

  // hide UP coupon prompt when isSubscriptionAdsEnabled is true
  return isUpCouponPromptEnabled && !isSubscriptionAdsEnabled;
}

export function useReferralPilotValue() {
  const isSubscriptionAdsEnabled = useIsSubscriptionAdsEnabled();
  const isUnifiedProductReferralEnabled = usePilotsValue('isUnifiedProductReferralEnabled');
  return !isSubscriptionAdsEnabled && isUnifiedProductReferralEnabled;
}

export function useShowInlineSwitchToExpert(isSignUp) {
  const isNewExpert = useUPNewExpertPilotValue();
  const isSubscriptionAdsProduct = useIsSubscriptionAdsProduct();
  return !isSubscriptionAdsProduct && isNewExpert && isSignUp;
}

export function useIsTestAccountCreationEnabled() {
  const isTestAccountCreationEnabled = usePilotsValue('isUnifiedProductTestAccountCreationEnabled');
  return isTestAccountCreationEnabled && isSiEnvironment(window.location.host);
}

export function useIsGoalAndWebsiteTileEnabled(isSignUp) {
  // useIsGoalAndWebsiteTileEnabled should be false when upsw is disabled (e.g. M365)
  const isUPStepWizardEnabled = useIsUPStepWizardEnabled();
  const isUPGoalAndWebsiteTileEnabled = usePilotsValue('isUPGoalAndWebsiteTileEnabled');
  return isUPGoalAndWebsiteTileEnabled && isUPStepWizardEnabled && isSignUp;
}
